import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MainLayout from "../../Layout/mainLayout";
import AddImage from "../../Global Functions/addImage";
import {
  editAmenities,
  viewAmenity,
} from "../../Redux/Actions/amenitiesResaleActions";
import toast from "react-hot-toast";
import "./editSubAmenities.css";

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const EditSubAmenities = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { amenity } = useSelector((state) => state.amenitiesResale || {});

  const [loading, setLoading] = useState(false);
  const [amenityData, setAmenityData] = useState({
    amenityName: "",
    position: "",
    amenitiesFor: "",
    image: "",
    id: id,
  });

  useEffect(() => {
    dispatch(viewAmenity(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (amenity?.data) {
      setAmenityData({
        amenityName: amenity?.data?.name || "",
        position: amenity?.data?.position || "",
        amenitiesFor: amenity?.data?.amenities_for || amenity?.data?.amenitiesFor || "",
        image: amenity?.data?.image || "",
        id: id,
      });
    }
  }, [amenity?.data, id]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageUrl = await AddImage(file);
    if (imageUrl) {
      setAmenityData((prev) => ({ ...prev, image: imageUrl }));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAmenityData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!amenityData.amenityName?.trim()) {
      return toast.error("Sub amenity name is required");
    }

    setLoading(true);

    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(amenityData).filter(([key, value]) => {
          if (key === "amenitiesFor") return value !== null && value !== undefined;
          return value !== null && value !== undefined && value !== "";
        })
      );

      const result = await dispatch(editAmenities(filteredPayload));

      if (result?.statusCode === 200) {
        toast.success(result?.message || "Sub amenity updated successfully");
        navigate(-1);
      } else {
        toast.error(result?.message || "Failed to update sub amenity");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="esb-wrapper">
        <div className="esb-header">
          <div className="esb-header-left">
            <div className="esb-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <div>
              <h1 className="esb-title">{amenity?.data?.name || "Edit Sub Amenity"}</h1>
              <p className="esb-subtitle">Update the selected sub amenity details</p>
            </div>
          </div>

          <button type="button" onClick={() => navigate(-1)} className="esb-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            View Sub-Amenities List
          </button>
        </div>

        <form className="esb-form" onSubmit={handleSubmit}>
          <div className="esb-section">
            <div className="esb-section-header">
              <span className="esb-section-badge">01</span>
              <h2 className="esb-section-title">Details & Icon</h2>
            </div>

            <div className="esb-grid esb-grid-2">
              <div className="esb-field">
                <label className="esb-label">Amenity For</label>
                <input
                  className="esb-input esb-input--readonly"
                  type="text"
                  value={amenityData.amenitiesFor || "No Data"}
                  disabled
                />
              </div>

              <div className="esb-field">
                <label className="esb-label">
                  Sub Amenity Name <span className="esb-req">*</span>
                </label>
                <input
                  className="esb-input"
                  type="text"
                  name="amenityName"
                  value={amenityData.amenityName}
                  onChange={handleChange}
                  placeholder="e.g. Indoor Pool"
                  disabled={loading}
                />
              </div>

              <div className="esb-field esb-field--full">
                <label className="esb-label">
                  Sub Amenity Icon <span className="esb-optional">(Optional)</span>
                </label>

                {amenityData.image ? (
                  <div className="esb-image-preview">
                    <img src={amenityData.image} alt="Sub amenity icon" className="esb-preview-img" />
                    <div className="esb-preview-info">
                      <span className="esb-preview-label">Icon available</span>
                      <button
                        type="button"
                        className="esb-remove-btn"
                        onClick={() => setAmenityData((prev) => ({ ...prev, image: "" }))}
                        disabled={loading}
                      >
                        <TrashIcon />
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="esb-upload-zone" htmlFor="edit-sub-amenity-image">
                    <input
                      id="edit-sub-amenity-image"
                      type="file"
                      name="image"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                      disabled={loading}
                    />
                    <div className="esb-upload-icon">
                      <UploadIcon />
                    </div>
                    <span className="esb-upload-text">Click to upload icon</span>
                    <span className="esb-upload-hint">PNG, JPG, SVG up to 2MB</span>
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="esb-footer">
            <div className="esb-footer-nav">
              <button type="submit" className="esb-btn esb-btn--submit" disabled={loading}>
                {loading ? (
                  "Updating..."
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Update Sub Amenity
                  </>
                )}
              </button>
            </div>

            <Link to="/amenities_resale" className="esb-btn esb-btn--cancel">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditSubAmenities;